import { IPlanPricing, BillingCycle, ISubscriptionPlan } from "../types/pricing";

export const PRICING_PLANS: IPlanPricing[] = [
    {
        planId: "starter",
        planName: "Starter",
        saasProductType: "Pro Membership AU",
        zuoraStatus: "Active",
        tier: "Base",

        monthlyPrice: {
            original: 99,
            discounted: 79,
            couponCode: "WEBDEAL",
            pmId: "PRO1",
            saasCheckoutType: "Casual",
        },
        annualPrice: {
            original: 990,
            discounted: 790,
            totalSavings: 398,
            couponCode: "SAVE200",
            pmId: "PRO4",
            saasCheckoutType: "Upfront",
        },
        features: [
            "Investar Search",
            "Property Analyser",
            "Portfolio Tracker",
            "Development Search",
            "Suburb Performance Reports",
            "Investment Calculators",
            "Email and live chat support",
            "On-demand video tutorials & training webinars"
        ],
        logoUrl: "/assets/logos/rei-logo.png",
        description: "Perfect for getting started with property investment research",
    },
    {
        planId: "professional",
        planName: "Professional",
        saasProductType: "Pro Membership AU RP Data",
        zuoraStatus: "Active",
        tier: "Mid",
        monthlyPrice: {
            original: 149,
            discounted: 129,
            couponCode: "RPSAVE20",
            pmId: "PRORP1",
            saasCheckoutType: "Casual",
        },
        annualPrice: {
            original: 1490,
            discounted: 1290,
            totalSavings: 498,
            couponCode: "RP1SAVE200",
            pmId: "PRORP4",
            saasCheckoutType: "Upfront",
        },
        features: [
            "Investar Search",
            "Property Analyser",
            "Portfolio Tracker",
            "Suburb Performance Reports",
            "Investment Calculators",
            "Nationwide CoreLogic RP Data Professional",
            "RP Data Mobile App",
            "Email, live chat and phone support",
            "On-demand video tutorials & training webinars",
        ],
        logoUrl: "../images/REI_Professional.png",
        description: "Most popular plan with comprehensive data access",
        isPopular: true,
    },
    {
        planId: "advanced",
        planName: "Advanced",
        saasProductType: "Pro Membership AU RP Data Archistar",
        zuoraStatus: "Active",
        tier: "Premium",
        monthlyPrice: {
            original: 248,
            discounted: 228,
            couponCode: "RPSAVE20",
            pmId: "PRORPA1",
            saasCheckoutType: "Casual",
        },
        annualPrice: {
            original: 2480,
            discounted: 2280,
            totalSavings: 696,
            couponCode: "RP1SAVE200",
            pmId: "PRORPA1Y",
            saasCheckoutType: "Upfront",
        },
        features: [
            "Investar Search",
            "Property Analyser",
            "Portfolio Tracker",
            "Development Search",
            "Suburb Performance Reports",
            "Investment Calculators",
            "Nationwide CoreLogic RP Data Professional",
            "RP Data Mobile App",
            "Archistar - discover profitable, underdeveloped sites",
            "Email, live chat and phone support",
            "On-demand video tutorials & training webinars",
        ],
        logoUrl: "/assets/logos/rei-rpdata-archistar-logo.png",
        description: "Complete solution for professional property developers",
    },
];

export const getPlanConfiguration = (planId: string, billingCycle: BillingCycle, coupon?: string | null): ISubscriptionPlan | null => {
    const plan = PRICING_PLANS.find((p) => p.planId === planId);
    if (!plan) return null;

    const isAnnual = billingCycle === "annual";
    const pricing = isAnnual ? plan.annualPrice : plan.monthlyPrice;
    const pmId = isAnnual ? plan.annualPrice.pmId : plan.monthlyPrice.pmId;

    // Apply discount only if the provided coupon matches the plan's coupon code
    const hasCoupon = coupon && coupon.toUpperCase() === pricing.couponCode?.toUpperCase();
    const finalPrice = hasCoupon ? pricing.discounted : pricing.original;

    return {
        ...plan,
        billingCycle,
        displayPrice: finalPrice,
        originalPrice: pricing.original,
        savingsAmount: hasCoupon ? (pricing.original - pricing.discounted) : 0,
        couponCode: hasCoupon ? pricing.couponCode : "",
        signupUrl: generateSignupUrl(plan.planId, pmId, coupon || "", billingCycle),
        pmId: pmId,
    };
};

const generateSignupUrl = (planId: string, pmId: string, couponCode: string, billingCycle: BillingCycle): string => {
    const baseUrls: Record<string, string> = {
        starter: "/signup/starter",
        professional: "/signup/professional",
        advanced: "/signup/advanced",
    };

    const params = new URLSearchParams({
        pmId,
        coupon: couponCode,
        billingCycle,
    });

    return `${baseUrls[planId]}?${params.toString()}`;
};

export const calculateDiscountPercentage = (original: number, discounted: number): number => {
    return Math.round(((original - discounted) / original) * 100);
};
